import MalformedRangeError from '../../../Errors/MalformedRangeError';
import Decimal from 'decimal.js';
import DecimalPrecisionError from '../../../Errors/DecimalPrecisionError';
import InvalidInputError from '../../../Errors/InvalidInputError';
import INumericValue from '../index';
/**
 * If maximum is undefined make it Infinity to make internal calculations easier. If the range
 * is discrete normalize the maximum by finding the largest valid value on the given increment.
 * @param minimum minimum value for the range
 * @param maximum maximum value for the range
 * @param increment increment for the range
 * @return Normalized maximum
 */

function parseMaximum(
  minimum: Decimal,
  maximum?: string | number | Decimal,
  increment?: string | number | Decimal,
): Decimal {
  if (maximum instanceof Decimal && maximum.isNaN()) {
    throw new MalformedRangeError('The range maximum cannot be NaN');
  }
  if (maximum === null || maximum === undefined) {
    return new Decimal(Infinity);
  }

  let maximumDecimal;
  try {
    maximumDecimal = new Decimal(maximum);

    return maximumDecimal;
  } catch {
    throw new MalformedRangeError(`The range maximum "${maximum}" is not a valid value.`);
  }
}

function parseIncrement(
  minimum: Decimal,
  maximum: Decimal,
  increment?: string | number | Decimal,
) {
  if (increment instanceof Decimal &&  !increment.isFinite()) {
    throw new MalformedRangeError('The range increment must be a finite value');
  }

  try {
    // If the range has a single value (i.e. min === max), then it should have no increment
    return increment !== null && increment !== undefined && increment.valueOf() !== 0 &&  !minimum.equals(maximum)
      ? new Decimal(increment)
      : undefined;
  } catch {
    throw new MalformedRangeError(`The range increment "${increment}" is not a valid value.`);
  }
}

function parseMinimum(minimum: string | number | Decimal) {
  if (minimum instanceof Decimal &&  !minimum.isFinite()) {
    throw new MalformedRangeError('The range minimum must be a finite value');
  }
  try {
    return new Decimal(minimum);
  } catch {
    throw new MalformedRangeError(`The range minimum "${minimum}" is not a valid value.`);
  }
}

/**
 * Maximum precision set on number values.
 * Originally this restriction was due to Javascript floating point math. Now it's more of an artificial
 * limitation but anything beyond 8 decimals isn't worth much anyway so keep this restriction for now.
 */
const MAXIMUM_DECIMAL_PRECISION = 8;
export const EXCEEDED_PRECISION_MESSAGE = `maximum number of decimal places supported is ${MAXIMUM_DECIMAL_PRECISION}`;

function validate(minimum: Decimal, maximum: Decimal, increment?: Decimal) {
  if (minimum === undefined) {
    throw new MalformedRangeError('minimum is required');
  }

  if (maximum !== undefined && minimum.greaterThan(maximum)) {
    throw new MalformedRangeError('minimum must be less than or equal to maximum');
  }

  if (increment && increment.lessThan(0)) {
    throw new MalformedRangeError('increment must be positive');
  }

  if (exceedsMaximumPrecision(minimum)) {
    throw new DecimalPrecisionError(EXCEEDED_PRECISION_MESSAGE);
  }

  if (maximum !== undefined && maximum.isFinite() && exceedsMaximumPrecision(maximum)) {
    throw new DecimalPrecisionError(EXCEEDED_PRECISION_MESSAGE);
  }

  if (increment !== undefined && exceedsMaximumPrecision(increment)) {
    throw new DecimalPrecisionError(EXCEEDED_PRECISION_MESSAGE);
  }
}

/**
 * Check whether the given value exceeds our maximum precision
 * @param value - The value to check
 * @returns True if the value exceeds maximum precision, otherwise false
 */
export function exceedsMaximumPrecision(value: Decimal | number): boolean {
  const decimalValue = new Decimal(value);
  const roundedValue = decimalValue.toDecimalPlaces(MAXIMUM_DECIMAL_PRECISION);
  return !roundedValue.add(0).equals(decimalValue);
}

/**
 * Interface that represents adjacent values to a searched value if the value is not in Range(s).
 */
export interface IAdjacentValues {
  /**
   * @property lowerAdjValue - The immediate value in the range(s) that is less than searched value.
   * @property upperAdjValue - The immediate value in the range(s) that is greater than searched value.
   */
  lowerAdjValue?: Decimal|undefined;
  upperAdjValue?: Decimal|undefined;

}

/**
 * Value that represents a numeric Range
 */
export default class Range implements INumericValue {

  /**
   * @property minimum - The minimum Range value
   * @property maximum - The maximum Range value
   * @property increment - The Range increment, Range is continuous if there is none
   */
  public minimum: Decimal;
  public maximum: Decimal;
  public increment?: Decimal;

  /**
   * Creates a new Range model.
   * @param minimum - the minimum value for the range.
   * @param maximum - the maximum value for the range, optional. If omitted, the range is unbounded.
   * @param increment - the increment for the range, optional. If omitted, the range is continuous.
   * @throws DecimalPrecisionError
   *    - if any of the values for minimum, maximum or increment has a decimal precision greater than 8
   * @throws MalformedRangeError
   *    - if any of the the values for minimum, maximum or increment are invalid.
   */
  constructor(
    minimum: string | number | Decimal,
    maximum?: string | number | Decimal,
    increment?: string | number | Decimal,
  ) {
    this.minimum = parseMinimum(minimum);
    this.maximum = parseMaximum(this.minimum, maximum, increment);
    this.increment = parseIncrement(this.minimum, this.maximum, increment);

    validate(this.minimum, this.maximum, this.increment);
  }

  public getMinimum(): Decimal {
    return this.minimum;
  }

  public getMaximum(): Decimal {
    return this.maximum;
  }

  public getIncrement(): Decimal | undefined {
    return this.increment;
  }

  /**
   * Whether this range contains the given value.
   * @param value {Decimal|Number|String} - The value which may be contained by this range.
   * @returns {IRangeContainsResponse} with contains true when the value is within the range, otherwise contains false with adjacent values.
   * @throws InvalidInputError if the input value is not valid
   * @throws DecimalPrecisionError if the input has more than the specified precision
   */
  public contains(value: string|number|Decimal): boolean {
    let isContainedBy;
    let valueBigNum: Decimal;
    try {
      valueBigNum = new Decimal(value);
    } catch {
      throw new InvalidInputError(`The value "${value}" is not a valid input`);
    }
    if (valueBigNum !== undefined) {
      if (exceedsMaximumPrecision(valueBigNum)) {
        throw new DecimalPrecisionError(EXCEEDED_PRECISION_MESSAGE);
      }
      isContainedBy = this.minimum.lessThanOrEqualTo(valueBigNum) && this.maximum.greaterThanOrEqualTo(valueBigNum);
      if (this.increment !== undefined) {
        let valueOffSetFromMin;
        valueOffSetFromMin = Decimal.sub(valueBigNum, this.minimum);
        isContainedBy = isContainedBy && valueOffSetFromMin.greaterThanOrEqualTo(0) &&  valueOffSetFromMin.mod(this.increment).isZero()
          && valueOffSetFromMin.mod(this.increment).isZero();
      }
    } else {
      throw new InvalidInputError('A valid value is required to check if the range contains it');
    }
    return isContainedBy;
  }

  /**
   * Method to find the adjacent values in a Range for the input value.
   * @param value {Decimal|Number|String} - The value for which the adjacent values are being calculated.
   * @returns {IAdjacentValues} The values in Range that are adjacent to the input value.
   * @throws InvalidInputError - When the value is contained in the range.
   */
  public calculateNearestValues(value: Decimal|number|string): IAdjacentValues {
    /* This method has to account for if the value is less that the minimum,
    greater than maximum, within the range and not a multiple of the increment.
     */
    const adjacentValues : IAdjacentValues = {};
    let decimalValue :Decimal;

    try {
      decimalValue = new Decimal(value);
    } catch (e) {
      throw new InvalidInputError('The input value is invalid');
    }

    if (this.increment) {
      if (decimalValue.greaterThanOrEqualTo(this.minimum)) {
        // If the value is beyond maximum we get the lower adj value from maximum and upper adj value is undefined
        let lowerRefVal: Decimal = decimalValue;
        if (decimalValue.greaterThanOrEqualTo(this.maximum)) {
          lowerRefVal = this.maximum;
          adjacentValues.upperAdjValue = undefined;
        }
        const incrMultipleFloor = lowerRefVal.sub(this.minimum).dividedToIntegerBy(this.increment);
        adjacentValues.lowerAdjValue = incrMultipleFloor.mul(this.increment).add(this.minimum);

        // if the value is already an increment, it will return an error
        if (adjacentValues.lowerAdjValue.equals(decimalValue)) {
          throw new InvalidInputError('The value is already present in the range');
        }
        // if the value is less than maximum and yet the next adjacent increment is beyond maximum then upperAdjValue is undefined
        if (decimalValue.lessThan(this.maximum)) {
          let upperAdjValue: Decimal | undefined = incrMultipleFloor.add(1).mul(this.increment).add(this.minimum);
          // The calculated upper adjacent value can be greater than maximum in scenarios where the increment after the value
          // is greater than maximum. Eg : 20-40#6, val:39
          if (upperAdjValue.greaterThan(this.maximum)) {
            upperAdjValue = undefined;
          }
          adjacentValues.upperAdjValue = upperAdjValue;
        }
      } else {
        adjacentValues.lowerAdjValue = undefined;
        adjacentValues.upperAdjValue = this.minimum;
      }
    } else if (decimalValue.greaterThan(this.maximum)) {
      adjacentValues.lowerAdjValue = this.maximum;
      adjacentValues.upperAdjValue = undefined;
    } else if (decimalValue.lessThan(this.minimum)) {
      adjacentValues.lowerAdjValue = undefined;
      adjacentValues.upperAdjValue = this.minimum;
    } else {
      throw new InvalidInputError('The value is already present in the range');
    }

    return adjacentValues;
  }

  /**
   * A number literal that is equivalent to a single value range needs to be identifiable via string equality.
   * Therefore, we need to ensure that when we have a single value we toString as such.
   */
  public toString(): string {
    if (this.minimum === this.maximum) {
      return `${this.minimum}`;
    }
    return `${this.minimum}-${this.maximum}#${this.increment}`;

  }
}
